import { ChartDataset } from "chart.js";

import { postTrackingData } from "@/api/endpoints/track";
import Line from "@/components/Charts/Line";
import { useI18n } from "@/contexts/i18nContext";
import { EVENT } from "@/tracking_events";
import ClickHandlerPlugin from "@components/Charts/plugins/ClickHandlerPlugin";
import { dynamicIntervalLabels } from "@components/Charts/utils/dynamicIntervalLabels";
import prepareDatasets from "@components/Charts/utils/prepareDataset";

import { BaseWidget } from "../BaseWidget";
import {
  PackedWidgetData,
  tooltipWithDateRangeCallback,
  unpackWidgetData,
} from "./utils";
import { BaseWidgetProps, WidgetProps } from "./WidgetProps";

type ChartDataType = ChartDataset<"line", number[]>;
type TopicsOverTimeData = PackedWidgetData<ChartDataType>;
type TopicsOverTimeWidgetProps = WidgetProps<TopicsOverTimeData>;
type ExternalTopicsOverTimeWidgetProps = BaseWidgetProps &
  TopicsOverTimeWidgetProps;

const TopicsOverTime: React.FunctionComponent<
  ExternalTopicsOverTimeWidgetProps
> = (props) => {
  const { data, filters } = props;
  const chartData = prepareDatasets(unpackWidgetData<ChartDataType>(data), {
    area: true,
  });

  const { formatDate } = useI18n();

  const hasData = data?.some(({ results }) => results?.dataset?.length);

  return (
    <BaseWidget
      // The types here should be fixed within BaseWidget. That is out of scope for now.
      hasData={hasData}
      {...props}
      title={L.REPORTS_WIDGET_DEFAULT_TITLE_INSIGHTS_TOPICS_OVER_TIME}
      description={
        L.REPORTS_WIDGET_DEFAULT_TITLE_INSIGHTS_TOPICS_OVER_TIME_SUBTITLE
      }
    >
      {data?.length === 1 && (
        <Line
          options={{
            spanGaps: false,
            plugins: {
              click: {
                onClick: (appliedFilters) => {
                  postTrackingData(EVENT.WIDGET_CLICK, {
                    widget: props.metric,
                  });
                  props.fetchMentions_v2(appliedFilters);
                },
              },
              tooltip: {
                itemSort: (a, b) => (b.raw as number) - (a.raw as number),
                filter: (tooltipItem) => {
                  // Don't show any tooltip items that are zeroed out
                  return tooltipItem.raw == 0 ? false : true;
                },
                callbacks: {
                  title: tooltipWithDateRangeCallback(
                    chartData.labels,
                    data?.[0]?.meta?.time_period,
                    formatDate,
                    filters?.end_date
                  ),
                },
              },
            },
            scales: {
              y: {
                ticks: {
                  precision: 0, // forces integers
                },
                min: 0,
              },
              x: {
                type: "time",
                ticks: {
                  display: false,
                  source: "labels",
                },
                grid: {
                  drawTicks: true,
                },
                time: {
                  minUnit: "month",
                  tooltipFormat: "PP",
                },
              },
              x2: dynamicIntervalLabels(chartData.meta?.time_period),
            },
          }}
          data={{
            ...chartData,
            datasets: chartData.datasets.map((d) => ({
              ...d,
              pointRadius: 0,
              pointHoverRadius: 0,
            })),
          }}
          plugins={[ClickHandlerPlugin]}
        />
      )}
    </BaseWidget>
  );
};

export default TopicsOverTime;
